import React from 'react';

interface SlackMessageRendererProps {
  message: string;
  className?: string;
}

/**
 * Renders Slack markdown format to React components
 * Supports:
 * - Bold: *text*
 * - Italic: _text_
 * - Strikethrough: ~text~
 * - Code: `code`
 * - Links: <url|text> or <url>
 * - Line breaks
 */
export const SlackMessageRenderer: React.FC<SlackMessageRendererProps> = ({
  message,
  className = '',
}) => {
  if (!message) return null;

  const parseSlackMarkdown = (text: string): React.ReactNode[] => {
    const parts: React.ReactNode[] = [];

    // Regular expressions for Slack markdown
    const patterns = [
      // Links: <url|text> or <url>
      { regex: /<([^|>]+)\|([^>]+)>/g, type: 'link' },
      { regex: /<([^>]+)>/g, type: 'url' },
      // Code: `code`
      { regex: /`([^`]+)`/g, type: 'code' },
      // Bold: *text* (but not **text** which is not Slack format)
      { regex: /(?<!\*)\*(?!\*)([^*]+?)\*(?!\*)/g, type: 'bold' },
      // Italic: _text_
      { regex: /(?<!_)_(?!_)([^_]+?)_(?!_)/g, type: 'italic' },
      // Strikethrough: ~text~
      { regex: /~([^~]+?)~/g, type: 'strikethrough' },
    ];

    // Find all matches with their positions
    const matches: Array<{
      start: number;
      end: number;
      type: string;
      content: string;
      url?: string;
    }> = [];

    patterns.forEach(({ regex, type }) => {
      let match;
      regex.lastIndex = 0; // Reset regex
      while ((match = regex.exec(text)) !== null) {
        const start = match.index;
        const end = match.index + match[0].length;

        // Check if this match overlaps with an existing match
        const overlaps = matches.some(
          m => (start >= m.start && start < m.end) || (end > m.start && end <= m.end)
        );

        if (!overlaps) {
          if (type === 'link') {
            matches.push({
              start,
              end,
              type,
              content: match[2], // Display text
              url: match[1], // URL
            });
          } else if (type === 'url') {
            matches.push({
              start,
              end,
              type,
              content: match[1], // URL
              url: match[1], // URL
            });
          } else {
            matches.push({
              start,
              end,
              type,
              content: match[1], // Content
            });
          }
        }
      }
    });

    // Sort matches by start position
    matches.sort((a, b) => a.start - b.start);

    // Build React nodes
    let lastIndex = 0;

    matches.forEach(match => {
      // Add text before match
      if (match.start > lastIndex) {
        const textBefore = text.substring(lastIndex, match.start);
        if (textBefore) {
          parts.push(textBefore);
        }
      }

      // Add the formatted content
      switch (match.type) {
        case 'bold':
          parts.push(<strong key={`${match.start}-bold`}>{match.content}</strong>);
          break;
        case 'italic':
          parts.push(<em key={`${match.start}-italic`}>{match.content}</em>);
          break;
        case 'strikethrough':
          parts.push(
            <span key={`${match.start}-strike`} style={{ textDecoration: 'line-through' }}>
              {match.content}
            </span>
          );
          break;
        case 'code':
          parts.push(
            <code
              key={`${match.start}-code`}
              className="bg-gray-200 px-1 py-0.5 rounded text-xs font-mono"
            >
              {match.content}
            </code>
          );
          break;
        case 'link':
        case 'url':
          parts.push(
            <a
              key={`${match.start}-link`}
              href={match.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-800 underline"
            >
              {match.type === 'link' ? match.content : match.url}
            </a>
          );
          break;
      }

      lastIndex = match.end;
    });

    // Add remaining text
    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex));
    }

    // Split by newlines and wrap in divs
    const result: React.ReactNode[] = [];
    parts.forEach((part, index) => {
      if (typeof part === 'string') {
        // Split string parts by newlines
        const lines = part.split('\n');
        lines.forEach((line, lineIndex) => {
          if (lineIndex > 0) {
            result.push(<br key={`br-${index}-${lineIndex}`} />);
          }
          if (line) {
            result.push(line);
          }
        });
      } else {
        result.push(part);
      }
    });

    return result;
  };

  const renderedContent = parseSlackMarkdown(message);

  return (
    <div className={`text-sm ${className}`} style={{ whiteSpace: 'pre-wrap' }}>
      {renderedContent}
    </div>
  );
};
